/**
 * HoOverviewTab - Overview tab for active project detail.
 * Matched exactly to extracted_assets_2: bf832ac1-9d48-45db-a651-9a4805854e40
 */

import type { ReactNode } from "react";
import { StudioButton } from "@/features/studio/theme";
import type { HoProject } from "../../../data/mock-homeowner";
import type { ActivityLog } from "../../../services/projects-api.service";
import type { IconName } from "../../../types/icon-name";
import { OVERDRAWN_ESCROW_NOTE } from "../../../utils/escrow-balance";
import type { HomeownerTabId } from "../../../utils/project-tab-keys";
import {
	Avatar,
	Btn,
	Eyebrow,
	FloatPanel,
	formatINR as fmt,
	Icn,
	PILL_TONES,
	Pill,
	secondaryBtnStyle,
} from "../design-primitives";

const ACTIVITY_ICONS: Record<string, IconName> = {
	"submitted milestone": "flag",
	"escrow funded": "wallet",
	"uploaded photos": "image",
	approved: "checkCircle",
	status_changed: "refreshCw",
};

interface HoOverviewTabStats {
	progress: number;
	inEscrow: number;
	spent: number;
	budget: number;
	daysLeft: number;
}

interface HoOverviewTabPills {
	inExecution: boolean;
	pendingApprovals: number;
}

interface TeamMemberSummary {
	name: string;
	ini: string;
	tone: "ink" | "cognac" | "terra" | "amber";
	role: string;
	type: "designer" | "contractor" | "homeowner";
}

interface HoOverviewTabProps {
	project: HoProject;
	activities?: ActivityLog[];
	stats?: HoOverviewTabStats;
	summary?: string;
	summaryPills?: HoOverviewTabPills;
	accent?: string;
	// The tab ids of the surface this tab is a body of, so a caller hands this
	// its own tab switcher with no cast. Widening it to `string` is what lets a
	// caller pass a handler that never sees an invalid key rejected.
	onNav?: (tab: HomeownerTabId) => void;
	teamMembers?: TeamMemberSummary[];
	/** Designer proposals still waiting for the homeowner to hire or decline. */
	proposalsAwaitingDecision?: number;
	/** The proposals could not be loaded, so their count is unknown. */
	proposalsUnavailable?: boolean;
	/** Shown below the overview, e.g. the review form once a project is complete. */
	reviewSlot?: ReactNode;
	/** The public/private choice, shown below the overview when there is one to make. */
	visibilitySlot?: ReactNode;
}

function formatActivityTime(dateStr: string): string {
	const date = new Date(dateStr);
	const now = new Date();
	const diff = now.getTime() - date.getTime();
	const days = Math.floor(diff / (1000 * 60 * 60 * 24));

	const time = date.toLocaleTimeString("en-US", {
		hour: "2-digit",
		minute: "2-digit",
		hour12: true,
	});

	if (days === 0) {
		return `${time} Today`;
	}
	if (days === 1) return `${time} Yesterday`;
	if (days > 1 && days < 7) {
		return `${time} · ${days} days ago`;
	}
	return `${time} · ${date.toLocaleDateString("en-IN", { day: "numeric", month: "short" })}`;
}

function formatActionTitle(action: string): string {
	if (!action) return "Unknown";
	return action
		.toUpperCase()
		.split("_")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(" ");
}

function transformActivity(apiActivity: ActivityLog) {
	const formattedAction = formatActionTitle(apiActivity.action);
	return {
		id: apiActivity.id,
		actor: "",
		action: formattedAction,
		target: "",
		time: formatActivityTime(apiActivity.createdAt),
	};
}

const DEFAULT_SUMMARY = "Project details will appear here once updated.";

export function HoOverviewTab({
	project,
	activities = [],
	stats,
	summary,
	summaryPills,
	accent = "var(--color-dashboard-accent)",
	onNav,
	teamMembers = [],
	proposalsAwaitingDecision = 0,
	proposalsUnavailable = false,
	reviewSlot,
	visibilitySlot,
}: HoOverviewTabProps) {
	const activity = activities.slice(0, 4).map(transformActivity);

	const progressPct = stats?.progress ?? project.pct;
	const escrow = stats?.inEscrow ?? project.escrow;
	const spentAmount = stats?.spent ?? project.spent;
	const budgetAmount = stats?.budget ?? project.budget;
	const daysLeft = stats?.daysLeft ?? project.days - project.day;
	const summaryText = summary || project.summary || DEFAULT_SUMMARY;

	const isInExecution =
		summaryPills?.inExecution ?? (project.pct > 0 && project.pct < 100);
	const pendingCount = summaryPills?.pendingApprovals ?? 0;

	return (
		<div
			className="grid grid-cols-1 md:grid-cols-[1fr_330px]"
			style={{ gap: 32, alignItems: "start" }}
		>
			{/* LEFT COLUMN */}
			<div style={{ minWidth: 0 }}>
				{proposalsUnavailable && (
					<FloatPanel
						elev={2}
						padding={20}
						style={{
							marginBottom: 20,
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							gap: 16,
							flexWrap: "wrap",
						}}
					>
						<div
							role="alert"
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 14.5,
								color: "var(--color-dashboard-text)",
							}}
						>
							Couldn't check for designer proposals.
						</div>
						<StudioButton
							variant="secondary"
							size="sm"
							onClick={() => onNav?.("team")}
							style={secondaryBtnStyle("sm")}
						>
							Open Team tab
						</StudioButton>
					</FloatPanel>
				)}

				{proposalsAwaitingDecision > 0 && (
					<FloatPanel
						elev={2}
						padding={20}
						style={{
							marginBottom: 20,
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							gap: 16,
							flexWrap: "wrap",
						}}
					>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: 10,
								fontFamily: "var(--font-body)",
								fontSize: 14.5,
								color: "var(--color-dashboard-text)",
							}}
						>
							<Icn name="inbox" size={16} color={accent} />
							{proposalsAwaitingDecision === 1
								? "1 designer proposal is waiting for your decision."
								: `${proposalsAwaitingDecision} designer proposals are waiting for your decision.`}
						</div>
						<Btn variant="primary" size="sm" onClick={() => onNav?.("team")}>
							Review proposals
						</Btn>
					</FloatPanel>
				)}

				{/* Summary panel */}
				<FloatPanel elev={2} padding={28} style={{ marginBottom: 20 }}>
					<div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
						{isInExecution && <Pill tone="active">In execution</Pill>}
						{pendingCount > 0 && (
							<Pill tone="awaiting">
								{pendingCount} approval pending{pendingCount > 1 ? "s" : ""}
							</Pill>
						)}
					</div>
					<p
						style={{
							fontFamily: "var(--font-body)",
							fontSize: 15.5,
							lineHeight: 1.65,
							color: "var(--color-dashboard-muted-dark)",
							margin: 0,
						}}
					>
						{summaryText}
					</p>
				</FloatPanel>

				{/* Stats: 4 cards */}
				<div
					className="grid grid-cols-2 md:grid-cols-[repeat(4,1fr)]"
					style={{ gap: 14, marginBottom: 20 }}
				>
					{[
						{
							label: "PROGRESS",
							value: `${progressPct}%`,
							sub: null as string | null,
							tone: "done" as const,
						},
						{
							// Negative when more has been paid out than was funded;
							// `getProjectPayments` does not clamp `totalHeld`.
							label: "IN ESCROW",
							value: fmt(escrow),
							sub: escrow < 0 ? OVERDRAWN_ESCROW_NOTE : "Held for you",
							tone: "awaiting" as const,
						},
						{
							label: "RELEASED",
							value: fmt(spentAmount),
							sub: `of ${fmt(budgetAmount)}`,
							tone: "active" as const,
						},
						{
							label: "HANDOVER",
							value: daysLeft > 0 ? `${daysLeft} days` : "Due",
							sub: daysLeft > 0 ? "remaining" : "now",
							tone: "neutral" as const,
						},
					].map((stat) => {
						const t = PILL_TONES[stat.tone];
						return (
							<FloatPanel key={stat.label} elev={1} padding={18}>
								<Eyebrow style={{ fontSize: 8 }}>{stat.label}</Eyebrow>
								<div
									style={{
										fontFamily: "var(--font-display)",
										fontWeight: 500,
										fontSize: 24,
										color: "var(--color-dashboard-text)",
										letterSpacing: "-0.015em",
										marginTop: 6,
									}}
								>
									{stat.value}
								</div>
								{stat.sub && (
									<div
										style={{
											display: "flex",
											alignItems: "center",
											gap: 5,
											marginTop: 6,
										}}
									>
										<span
											style={{
												width: 6,
												height: 6,
												borderRadius: "50%",
												background: t.dot,
											}}
										/>
										<span
											style={{
												fontFamily: "var(--font-mono)",
												fontSize: 8,
												letterSpacing: "0.08em",
												color: "var(--color-dashboard-muted)",
												textTransform: "uppercase",
											}}
										>
											{stat.sub}
										</span>
									</div>
								)}
							</FloatPanel>
						);
					})}
				</div>

				{/* Team Members */}
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "baseline",
						marginBottom: 14,
					}}
				>
					<Eyebrow>TEAM MEMBERS · {teamMembers.length}</Eyebrow>
					<button
						type="button"
						style={{
							fontFamily: "var(--font-body)",
							fontSize: 12.5,
							color: "var(--color-dashboard-muted)",
							cursor: "pointer",
							background: "none",
							border: "none",
							padding: 0,
						}}
						onClick={() => onNav?.("team")}
					>
						Go to teams →
					</button>
				</div>
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						gap: 10,
					}}
				>
					{teamMembers.length === 0 ? (
						<div
							style={{
								padding: "20px 0",
								fontFamily: "var(--font-body)",
								fontSize: 13,
								color: "var(--color-dashboard-muted)",
								textAlign: "center",
							}}
						>
							No team members yet
						</div>
					) : (
						teamMembers.map((member) => (
							<div
								key={`${member.type}-${member.ini}`}
								style={{
									display: "flex",
									alignItems: "center",
									gap: 12,
									padding: "12px 14px",
									background: "var(--color-dashboard-paper)",
									borderRadius: 7,
									border: "1px solid var(--color-dashboard-border-soft)",
								}}
							>
								<Avatar initials={member.ini} tone={member.tone} size={36} />
								<div style={{ flex: 1, minWidth: 0 }}>
									<div
										style={{
											fontFamily: "var(--font-display)",
											fontStyle: "italic",
											fontSize: 15,
											fontWeight: 500,
											color: "var(--color-dashboard-text)",
										}}
									>
										{member.name}
									</div>
									<div
										style={{
											fontFamily: "var(--font-mono)",
											fontSize: 8.5,
											letterSpacing: "0.1em",
											color: "var(--color-dashboard-muted)",
											textTransform: "uppercase",
											marginTop: 2,
										}}
									>
										{member.role}
									</div>
								</div>
								<Pill
									tone={
										member.type === "designer"
											? "active"
											: member.type === "contractor"
												? "neutral"
												: "done"
									}
									dot={false}
								>
									{member.type === "designer"
										? "Designer"
										: member.type === "contractor"
											? "Contractor"
											: "You"}
								</Pill>
							</div>
						))
					)}
				</div>
			</div>

			{/* RIGHT COLUMN — sticky sidebar */}
			<div
				style={{
					position: "sticky",
					top: 76,
					display: "flex",
					flexDirection: "column",
					gap: 18,
				}}
			>
				{/* Designer card */}
				{project.designer && (
					<FloatPanel elev={2} padding={22}>
						<Eyebrow style={{ fontSize: 9, marginBottom: 14 }}>
							YOUR DESIGNER
						</Eyebrow>
						<div style={{ display: "flex", alignItems: "center", gap: 13 }}>
							<Avatar
								initials={project.designerIni ?? ""}
								tone={project.designerTone ?? "ink"}
								size={48}
							/>
							<div>
								<div
									style={{
										fontFamily: "var(--font-display)",
										fontStyle: "italic",
										fontSize: 18,
										fontWeight: 500,
										color: "var(--color-dashboard-text)",
									}}
								>
									{project.designer}
								</div>
								<div
									style={{
										fontFamily: "var(--font-mono)",
										fontSize: 8.5,
										letterSpacing: "0.12em",
										color: "var(--color-dashboard-muted)",
										marginTop: 2,
									}}
								>
									{(project.studio ?? "").toUpperCase()}
								</div>
							</div>
						</div>
						<div style={{ display: "flex", gap: 8, marginTop: 16 }}>
							<StudioButton
								variant="secondary"
								size="sm"
								style={{ ...secondaryBtnStyle("sm"), flex: 1 }}
								onClick={() => onNav?.("messages")}
							>
								<Icn name="message" size={16} />
								Message
							</StudioButton>
							<Btn
								variant="ghost"
								size="sm"
								icon={<Icn name="phone" size={16} />}
							>
								Call
							</Btn>
						</div>
					</FloatPanel>
				)}

				{/* Recent activity */}
				<FloatPanel elev={1} padding={22}>
					<Eyebrow style={{ fontSize: 9, marginBottom: 14 }}>
						RECENT ACTIVITY
					</Eyebrow>
					{activity.length === 0 ? (
						<div
							style={{
								padding: "20px 0",
								fontFamily: "var(--font-body)",
								fontSize: 13,
								color: "var(--color-dashboard-muted)",
								textAlign: "center",
							}}
						>
							No recent activity
						</div>
					) : (
						activity.map((a, i) => (
							<div
								key={a.id}
								style={{
									display: "flex",
									gap: 11,
									padding: "10px 0",
									borderBottom:
										i < 3 ? "1px solid var(--color-dashboard-border-soft)" : 0,
								}}
							>
								<div
									style={{
										width: 28,
										height: 28,
										borderRadius: 6,
										background: "var(--color-dashboard-divider)",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										color: accent,
										flexShrink: 0,
									}}
								>
									<Icn
										name={ACTIVITY_ICONS[a.action] ?? "info"}
										size={14}
										color={accent}
									/>
								</div>
								<div style={{ flex: 1, minWidth: 0 }}>
									<div
										style={{
											fontFamily: "var(--font-body)",
											fontSize: 12.5,
											lineHeight: 1.45,
											color: "var(--color-dashboard-text)",
										}}
									>
										{a.actor} {a.action} {a.target}
									</div>
									<div
										style={{
											fontFamily: "var(--font-mono)",
											fontSize: 8,
											letterSpacing: "0.1em",
											color: "var(--color-dashboard-placeholder)",
											marginTop: 3,
											textTransform: "uppercase",
										}}
									>
										{a.time}
									</div>
								</div>
							</div>
						))
					)}
				</FloatPanel>
			</div>
			{visibilitySlot && <div className="md:col-span-2">{visibilitySlot}</div>}
			{reviewSlot && <div className="md:col-span-2">{reviewSlot}</div>}
		</div>
	);
}
